// Theme panel for external builder apps. Anchors as a popover next to the
// preview device toggle in BuilderEditor.
//
// Reads /index.css and /tailwind.config.js via the files API. The panel
// edits the parsed colors + font, then serializes back to both files using
// `buildIndexCss` / `buildTailwindConfig`. The runtime preview picks up the
// file changes via the editor's existing reload mechanism.
//
// Why files (not a DB column): external apps must be exportable as standard
// code (see [[feedback-external-app-exportable]]) — the same files that drive
// the in-platform preview also build under `npm run build` outside our
// platform.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "../../../components/LoadingButton";
import LucideIcon from "../../../components/LucideIcon";
import type { BuilderApiClient } from "../api";
import type { AppTheme, AppThemeColors } from "../types";
import {
  DEFAULT_THEME,
  THEME_TOKENS,
  buildIndexCss,
  buildTailwindConfig,
  hexToHslTriple,
  hslTripleToHex,
  parseIndexCss,
} from "../themeFiles";

const COMMON_FONTS = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Poppins",
  "Source Sans 3",
  "DM Sans",
  "Nunito",
  "Work Sans",
  "Montserrat",
];

// Tokens highlighted in the preview ribbon at the top — chosen to give a
// quick read of the overall palette without listing every token.
const PREVIEW_RIBBON_TOKENS: ReadonlyArray<keyof AppThemeColors> = [
  "primary",
  "background",
  "secondary",
  "accent",
  "ring",
  "foreground",
];

// Convert "card foreground" → "Card Foreground" so the row labels match the
// screenshot.
function titleCase(s: string): string {
  return s
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

interface ThemePanelProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  appId: string;
  api: BuilderApiClient;
  // Called after files are saved so the editor can refresh its file map and
  // trigger a runtime reload.
  onSaved: () => void;
}

const ThemePanel: React.FC<ThemePanelProps> = ({
  anchorEl,
  open,
  onClose,
  appId,
  api,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [theme, setTheme] = useState<AppTheme>(DEFAULT_THEME);
  // Preserve the existing .dark { ... } block verbatim so editing light doesn't
  // discard dark customizations the user may have made by hand.
  const [darkBlock, setDarkBlock] = useState<string | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"colors" | "fonts">("colors");
  // Hidden <input type="color"> driven by clicking the row swatch chip. The
  // native picker is the simplest cross-browser option and matches the
  // hex-value-plus-chip presentation in the screenshot.
  const colorInputRef = useRef<HTMLInputElement | null>(null);
  const [editingKey, setEditingKey] = useState<keyof AppThemeColors | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let css = "";
      try {
        const f = await api.readFile(appId, "/index.css");
        css = f.content || "";
      } catch {
        // Missing file — fall through to defaults.
      }
      if (css.trim()) {
        const parsed = parseIndexCss(css);
        setTheme({
          colors: parsed.colors,
          fonts: { sans: parsed.fontSans },
          radius: parsed.radius,
        });
        setDarkBlock(parsed.darkBlock);
      } else {
        setTheme(DEFAULT_THEME);
        setDarkBlock(undefined);
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load theme");
    } finally {
      setLoading(false);
    }
  }, [api, appId]);

  useEffect(() => {
    if (open) {
      load();
      setTab("colors");
    }
  }, [open, load]);

  const swatches = useMemo(
    () =>
      THEME_TOKENS.map((t) => ({
        ...t,
        label: titleCase(t.label),
        hex: hslTripleToHex(theme.colors[t.key]).toUpperCase(),
      })),
    [theme.colors],
  );

  const ribbonHexes = useMemo(
    () => PREVIEW_RIBBON_TOKENS.map((k) => hslTripleToHex(theme.colors[k])),
    [theme.colors],
  );

  const openColorPicker = (key: keyof AppThemeColors) => {
    setEditingKey(key);
    // Defer to let React commit the editingKey change before triggering click.
    requestAnimationFrame(() => colorInputRef.current?.click());
  };

  const handleColorChange = (hex: string) => {
    if (!editingKey) return;
    setTheme((t) => ({
      ...t,
      colors: { ...t.colors, [editingKey]: hexToHslTriple(hex) },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const indexCss = darkBlock !== undefined
        ? buildIndexCss(theme, darkBlock)
        : buildIndexCss(theme);
      const tailwindConfig = buildTailwindConfig(theme);
      await api.writeFile(appId, "/index.css", indexCss, "css");
      await api.writeFile(appId, "/tailwind.config.js", tailwindConfig, "js");
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Failed to save theme");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            width: 440,
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            borderRadius: 2,
          },
        },
      }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Theme
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ mt: -0.5, mr: -0.5 }}>
            <LucideIcon name="X" size={18} />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          These colors and fonts make up your theme. Any changes apply everywhere.
        </Typography>

        {/* Preview ribbon */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            borderRadius: 1.5,
            overflow: "hidden",
            border: 1,
            borderColor: "divider",
            height: 88,
          }}
        >
          {/* AaBb swatch sized larger than the rest */}
          <Box
            sx={{
              flex: 2,
              bgcolor: hslTripleToHex(theme.colors.primary),
              color: hslTripleToHex(theme.colors.primaryForeground),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: `${theme.fonts.sans}, sans-serif`,
              fontSize: 36,
              fontWeight: 600,
            }}
          >
            AaBb
          </Box>
          {ribbonHexes.slice(1).map((hex, i) => (
            <Box key={i} sx={{ flex: 1, bgcolor: hex }} />
          ))}
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ mt: 1.5, minHeight: 36, "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontWeight: 600, fontSize: 15 } }}
        >
          <Tab value="colors" label="Colors" />
          <Tab value="fonts" label="Fonts" />
        </Tabs>
      </Box>

      {/* Body */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 3,
          pb: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : tab === "colors" ? (
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}>
            {swatches.map((s) => (
              <Stack
                key={s.key}
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ py: 1.25 }}
              >
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {s.label}
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}
                  >
                    {s.hex}
                  </Typography>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => openColorPicker(s.key)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openColorPicker(s.key);
                      }
                    }}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                      bgcolor: s.hex,
                      cursor: "pointer",
                      transition: "transform 120ms",
                      "&:hover": { transform: "scale(1.05)" },
                    }}
                  />
                </Stack>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Font family
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={theme.fonts.sans}
                onChange={(e) =>
                  setTheme((t) => ({ ...t, fonts: { sans: e.target.value } }))
                }
                select
                SelectProps={{ native: true }}
                helperText="Loaded from Google Fonts when the app runs."
              >
                {COMMON_FONTS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </TextField>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Border radius
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={theme.radius}
                onChange={(e) => setTheme((t) => ({ ...t, radius: e.target.value }))}
                placeholder="0.625rem"
                helperText="Used by Tailwind's rounded-lg / -md / -sm utilities."
              />
            </Box>
          </Stack>
        )}
      </Box>

      {/* Hidden color picker — opened on swatch click */}
      <Box
        component="input"
        ref={colorInputRef}
        type="color"
        value={editingKey ? hslTripleToHex(theme.colors[editingKey]) : "#000000"}
        onChange={(e) => handleColorChange((e.target as HTMLInputElement).value)}
        sx={{
          position: "absolute",
          width: 0,
          height: 0,
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: error ? "space-between" : "flex-end",
          gap: 1,
          px: 3,
          py: 2,
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={saving}
            onClick={handleSave}
            disabled={loading}
          >
            Save & Apply
          </LoadingButton>
        </Stack>
      </Box>
    </Popover>
  );
};

export default ThemePanel;
